import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { FormEvent, MouseEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRequest } from '../../hooks/useRequest';
import {
  ContentfulTag,
  createContentfulTag,
  deleteContentfulTag,
  fetchContentfulTags,
  updateContentfulTag,
} from '../../utils/axios/api';

export function AdminContentfulTagsPage() {
  const { t } = useTranslation();
  const loadRequest = useRequest<ContentfulTag[]>();
  const saveRequest = useRequest<ContentfulTag>();
  const deleteRequest = useRequest();
  const [tags, setTags] = useState<ContentfulTag[]>([]);

  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<ContentfulTag | null>(null);
  const [contentfulTagId, setContentfulTagId] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const [tagToDelete, setTagToDelete] = useState<ContentfulTag | null>(null);

  useEffect(() => {
    void loadRequest.fetch(() => fetchContentfulTags()).then((data) => {
      if (!data) return;
      setTags(data);
    });
  }, [loadRequest]);

  const resetFormDialog = useCallback(() => {
    setEditingTag(null);
    setContentfulTagId('');
    setValidationError(null);
  }, []);

  const handleOpenCreateDialog = useCallback(() => {
    resetFormDialog();
    setIsFormDialogOpen(true);
  }, [resetFormDialog]);

  const handleOpenEditDialog = useCallback((tag: ContentfulTag) => {
    setEditingTag(tag);
    setContentfulTagId(tag.contentfulTagId);
    setValidationError(null);
    setIsFormDialogOpen(true);
  }, []);

  const handleCloseFormDialog = useCallback(() => {
    if (saveRequest.isLoading) {
      return;
    }

    setIsFormDialogOpen(false);
    resetFormDialog();
  }, [resetFormDialog, saveRequest.isLoading]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const trimmedId = contentfulTagId.trim();

      if (!trimmedId) {
        setValidationError(t('admin.contentfulTags.errors.required'));
        return;
      }

      setValidationError(null);

      if (editingTag) {
        const updated = await saveRequest.fetch(() =>
          updateContentfulTag(editingTag.id, {
            contentfulTagId: trimmedId,
          }),
        );
        if (!updated) {
          return;
        }

        setTags((prev) =>
          prev.map((item) => (item.id === updated.id ? updated : item)),
        );
      } else {
        const created = await saveRequest.fetch(() =>
          createContentfulTag({
            contentfulTagId: trimmedId,
          }),
        );
        if (!created) {
          return;
        }

        setTags((prev) => [created, ...prev]);
      }

      setIsFormDialogOpen(false);
      resetFormDialog();
    },
    [contentfulTagId, editingTag, resetFormDialog, saveRequest, t],
  );

  const handleOpenDeleteDialog = useCallback(
    (event: MouseEvent, tag: ContentfulTag) => {
      event.stopPropagation();
      event.preventDefault();
      setTagToDelete(tag);
    },
    [],
  );

  const handleCloseDeleteDialog = useCallback(() => {
    if (deleteRequest.isLoading) {
      return;
    }

    setTagToDelete(null);
  }, [deleteRequest.isLoading]);

  const handleDelete = useCallback(async () => {
    if (!tagToDelete) {
      return;
    }

    const result = await deleteRequest.fetch(async () => {
      await deleteContentfulTag(tagToDelete.id);
    });
    if (result === null) {
      return;
    }
    setTags((prev) => prev.filter((item) => item.id !== tagToDelete.id));
    setTagToDelete(null);
  }, [deleteRequest, tagToDelete]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Typography variant="subtitle1">
          {t('admin.contentfulTags.description')}
        </Typography>
        <Button variant="contained" onClick={handleOpenCreateDialog}>
          {t('admin.contentfulTags.create.button')}
        </Button>
      </Box>

      {loadRequest.isLoading ? (
        <CircularProgress size={24} />
      ) : loadRequest.exception && tags.length === 0 ? (
        <Typography variant="body2" color="error">
          {t(`exceptions.${loadRequest.exception}`)}
        </Typography>
      ) : tags.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('admin.contentfulTags.empty')}
        </Typography>
      ) : (
        tags.map((tag) => (
          <Box
            key={tag.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: '10px',
              bgcolor: 'background.paper',
              '&:hover': {
                borderColor: 'text.secondary',
                bgcolor: 'action.hover',
              },
            }}
          >
            <ButtonBase
              onClick={() => {
                handleOpenEditDialog(tag);
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                justifyContent: 'flex-start',
                px: 1.5,
                py: 1.25,
                borderRadius: '10px',
                textAlign: 'left',
                color: 'inherit',
              }}
            >
              <Typography variant="body1" noWrap>
                {tag.contentfulTagId}
              </Typography>
            </ButtonBase>
            <IconButton
              aria-label={t('admin.contentfulTags.delete.button')}
              color="error"
              onClick={(event) => {
                handleOpenDeleteDialog(event, tag);
              }}
              sx={{ mr: 0.5 }}
            >
              <DeleteOutlinedIcon />
            </IconButton>
          </Box>
        ))
      )}

      <Dialog
        open={isFormDialogOpen}
        onClose={handleCloseFormDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingTag
            ? t('admin.contentfulTags.edit.title')
            : t('admin.contentfulTags.create.title')}
        </DialogTitle>
        <Box
          component="form"
          onSubmit={(event) => {
            void handleSubmit(event);
          }}
        >
          <DialogContent
            sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
          >
            <TextField
              label={t('admin.contentfulTags.contentfulTagId')}
              value={contentfulTagId}
              onChange={(event) => {
                setContentfulTagId(event.target.value);
              }}
              disabled={saveRequest.isLoading}
              autoFocus
              required
              fullWidth
            />
            {validationError ?? saveRequest.exception ? (
              <Typography variant="body2" color="error">
                {validationError ??
                  t(`exceptions.${saveRequest.exception}`)}
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseFormDialog}
              disabled={saveRequest.isLoading}
            >
              {t('admin.contentfulTags.create.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saveRequest.isLoading}
            >
              {saveRequest.isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : editingTag ? (
                t('admin.contentfulTags.save')
              ) : (
                t('admin.contentfulTags.create.submit')
              )}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(tagToDelete)} onClose={handleCloseDeleteDialog}>
        <DialogTitle>{t('admin.contentfulTags.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admin.contentfulTags.delete.confirm', {
              id: tagToDelete?.contentfulTagId ?? '',
            })}
          </DialogContentText>
          {deleteRequest.exception ? (
            <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
              {t(`exceptions.${deleteRequest.exception}`)}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={deleteRequest.isLoading}
          >
            {t('admin.contentfulTags.delete.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteRequest.isLoading}
            onClick={() => {
              void handleDelete();
            }}
          >
            {deleteRequest.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('admin.contentfulTags.delete.button')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
